import {
  ALERTAS,
  MOTIVOS_PAUSA,
  TIPOS_ALERTA,
  aceitaMotivo,
  type AlertaHistorico,
  type RegraAlerta,
  type SetorMonitorado,
  type TipoAlerta,
} from '@mymaxx/shared';
import { Aviso, Botao } from '@mymaxx/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { History, SlidersHorizontal, X } from 'lucide-react';
import { useState } from 'react';
import { ErroApi } from '../lib/api';
import { supervisaoApi } from '../lib/supervisao-api';

/** Uma linha da lista de regras: um tipo de alerta, com ou sem recorte por motivo. */
interface LinhaDeRegra {
  tipo: TipoAlerta;
  /** Vazio na linha geral do tipo; o motivo da pausa nas linhas filhas. */
  motivo: string;
  rotulo: string;
  ajuda: string;
  padraoMinutos: number;
  unidade: string;
}

/**
 * A lista inteira, já desdobrada.
 *
 * "Em pausa há muito tempo" vira uma linha por motivo de pausa (pedido de 30/09): meia
 * hora de almoço é o combinado, meia hora de banheiro não é. Com um número só, o líder
 * escolhia entre ser avisado tarde ou ser incomodado todo almoço — e acabava desligando o
 * alerta inteiro.
 *
 * A linha sem motivo continua existindo e vale para o resto: ligar só ela é o
 * comportamento de antes, e quem não quiser detalhar não precisa mexer em nada.
 */
const LINHAS: LinhaDeRegra[] = TIPOS_ALERTA.flatMap((tipo) => {
  const d = ALERTAS[tipo];
  const geral: LinhaDeRegra = { tipo, motivo: '', ...d };
  if (!aceitaMotivo(tipo)) return [geral];
  return [
    {
      ...geral,
      rotulo: `${d.rotulo} (qualquer motivo)`,
      ajuda: 'Vale para as pausas que não tiverem um tempo próprio abaixo.',
    },
    ...MOTIVOS_PAUSA.map(({ rotulo: motivo }) => ({
      tipo,
      motivo,
      rotulo: `Pausa: ${motivo}`,
      ajuda: `Só quando o motivo escolhido for "${motivo}".`,
      padraoMinutos: d.padraoMinutos,
      unidade: d.unidade,
    })),
  ];
});

/**
 * Onde o líder escolhe o que quer ser avisado, por setor.
 *
 * As regras são dele e de mais ninguém: dois supervisores do mesmo setor podem querer
 * limites diferentes, e o apoio N2 que acompanha a Liberação não tem por que herdar o
 * gosto do supervisor do Suporte.
 *
 * Um setor de cada vez na tela. Mostrar os seis tipos vezes todos os setores de uma vez
 * viraria uma parede de campos que ninguém lê antes de clicar em salvar.
 */
export function ConfigurarAlertas({
  setores,
  aoFechar,
}: {
  setores: SetorMonitorado[];
  aoFechar: () => void;
}) {
  const qc = useQueryClient();
  const [setorId, setSetorId] = useState(setores[0]?.id ?? '');
  const [aba, setAba] = useState<'regras' | 'historico'>('regras');
  const [erro, setErro] = useState<string | null>(null);

  const regras = useQuery({ queryKey: ['regras-alerta'], queryFn: supervisaoApi.regrasDeAlerta });
  const historico = useQuery({
    queryKey: ['historico-alertas', setorId],
    queryFn: () => supervisaoApi.historicoDeAlertas(setorId || undefined),
    enabled: aba === 'historico',
  });

  const salvar = useMutation({
    mutationFn: supervisaoApi.salvarRegraDeAlerta,
    onSuccess: () => {
      setErro(null);
      void qc.invalidateQueries({ queryKey: ['regras-alerta'] });
      void qc.invalidateQueries({ queryKey: ['alertas'] });
    },
    onError: (e) => setErro(e instanceof ErroApi ? e.mensagem : 'Não consegui salvar'),
  });
  const remover = useMutation({
    mutationFn: supervisaoApi.removerRegraDeAlerta,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['regras-alerta'] });
      void qc.invalidateQueries({ queryKey: ['alertas'] });
    },
  });

  const doSetor = (tipo: TipoAlerta, motivo: string): RegraAlerta | undefined =>
    regras.data?.find((r) => r.setorId === setorId && r.tipo === tipo && r.motivo === motivo);

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="my-8 w-full max-w-3xl rounded-card bg-white p-6 shadow-xl dark:bg-neutral-900">
        <header className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-lg font-medium">Meus alertas</h2>
            <p className="text-sm text-neutral-500">
              O que você quer que apareça na sua tela, e a partir de quando. Vale só para você — e
              cada setor tem o seu ritmo.
            </p>
          </div>
          <button type="button" onClick={aoFechar} aria-label="Fechar" className="text-neutral-500">
            <X size={20} />
          </button>
        </header>

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <select
            aria-label="Setor"
            value={setorId}
            onChange={(e) => setSetorId(e.target.value)}
            className="rounded-lg border border-neutral-300 px-3 py-1.5 text-sm dark:border-neutral-700 dark:bg-neutral-950"
          >
            {setores.map((s) => (
              <option key={s.id} value={s.id}>
                {s.nome}
              </option>
            ))}
          </select>
          <div className="ml-auto flex gap-1 text-sm">
            <button
              type="button"
              onClick={() => setAba('regras')}
              className={`flex items-center gap-1 rounded-lg px-3 py-1.5 ${aba === 'regras' ? 'bg-neutral-100 font-medium dark:bg-neutral-800' : 'text-neutral-500'}`}
            >
              <SlidersHorizontal size={14} /> Regras
            </button>
            <button
              type="button"
              onClick={() => setAba('historico')}
              className={`flex items-center gap-1 rounded-lg px-3 py-1.5 ${aba === 'historico' ? 'bg-neutral-100 font-medium dark:bg-neutral-800' : 'text-neutral-500'}`}
            >
              <History size={14} /> Histórico
            </button>
          </div>
        </div>

        {erro && <Aviso tipo="erro">{erro}</Aviso>}

        {aba === 'regras' && (
          <ul className="space-y-2">
            {LINHAS.map((linha) => {
              const regra = doSetor(linha.tipo, linha.motivo);
              return (
                <li
                  key={`${linha.tipo}|${linha.motivo}`}
                  className={`flex flex-wrap items-center gap-3 rounded-card border border-neutral-200 p-3 dark:border-neutral-800 ${
                    // As pausas com motivo entram recuadas: são variações da linha de cima,
                    // não oito alertas diferentes.
                    linha.motivo ? 'ml-6' : ''
                  }`}
                >
                  <label className="flex min-w-0 flex-1 items-start gap-2">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={!!regra?.ativa}
                      onChange={(e) => {
                        if (!e.target.checked && regra) {
                          remover.mutate(regra.id);
                          return;
                        }
                        salvar.mutate({
                          setorId,
                          tipo: linha.tipo,
                          motivo: linha.motivo,
                          minutos: regra?.minutos ?? linha.padraoMinutos,
                          ativa: true,
                        });
                      }}
                    />
                    <span className="min-w-0">
                      <span className="text-sm font-medium">{linha.rotulo}</span>
                      <span className="block text-xs text-neutral-500">{linha.ajuda}</span>
                    </span>
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={1}
                      max={1440}
                      aria-label={`Minutos para ${linha.rotulo}`}
                      disabled={!regra}
                      // O `key` é o que faz o campo mostrar o valor salvo ao reabrir. Ele é
                      // não controlado (o valor só sobe ao sair do campo, senão digitar
                      // "15" passaria por "1" e dispararia alerta no meio da digitação), e
                      // campo não controlado ignora mudança de `defaultValue`: a tela
                      // continuava exibindo 30 depois de salvar 1, embora o alerta já
                      // usasse 1. Com o `key`, o React recria o campo quando o valor muda.
                      key={`${setorId}-${linha.tipo}-${linha.motivo}-${regra?.minutos ?? 'padrao'}`}
                      defaultValue={regra?.minutos ?? linha.padraoMinutos}
                      // Salva ao sair do campo, e não a cada tecla: digitar "15" passaria
                      // por "1" e o alerta dispararia no meio da digitação.
                      onBlur={(e) => {
                        const minutos = Number(e.target.value);
                        if (!regra || !minutos || minutos === regra.minutos) return;
                        salvar.mutate({
                          setorId,
                          tipo: linha.tipo,
                          motivo: linha.motivo,
                          minutos,
                          ativa: true,
                        });
                      }}
                      className="w-20 rounded-lg border border-neutral-300 px-2 py-1 text-sm disabled:opacity-40 dark:border-neutral-700 dark:bg-neutral-950"
                    />
                    <span className="text-xs text-neutral-500">{linha.unidade}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {aba === 'historico' && (
          <div className="max-h-[55vh] overflow-y-auto rounded-card border border-neutral-200 dark:border-neutral-800">
            {historico.isPending && <p className="p-4 text-sm text-neutral-500">Carregando…</p>}
            {historico.data?.length === 0 && (
              <p className="p-4 text-sm text-neutral-500">Nada disparou para você ainda.</p>
            )}
            {!!historico.data?.length && (
              <table className="w-full text-left text-sm">
                <thead className="sticky top-0 bg-neutral-50 text-xs text-neutral-500 dark:bg-neutral-800">
                  <tr>
                    <th className="p-2 font-medium">Data</th>
                    <th className="p-2 font-medium">Tipo</th>
                    <th className="p-2 font-medium">Descrição</th>
                    <th className="p-2 font-medium">Durou</th>
                  </tr>
                </thead>
                <tbody>
                  {historico.data.map((h: AlertaHistorico) => (
                    <tr key={h.id} className="border-t border-neutral-200 dark:border-neutral-800">
                      <td className="p-2 whitespace-nowrap text-xs">
                        {new Date(h.disparadoEm).toLocaleString('pt-BR')}
                      </td>
                      <td className="p-2 text-xs">{ALERTAS[h.tipo].rotulo}</td>
                      <td className="p-2">{h.descricao}</td>
                      <td className="p-2 text-xs whitespace-nowrap">
                        {h.duracaoSeg === null ? (
                          <span className="text-amber-700 dark:text-amber-400">em aberto</span>
                        ) : (
                          `${Math.max(1, Math.round(h.duracaoSeg / 60))} min`
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        <div className="mt-4 flex justify-end">
          <Botao variante="secundario" onClick={aoFechar}>
            Fechar
          </Botao>
        </div>
      </div>
    </div>
  );
}
